/**
 * Custom hooks for auction data fetching and state management
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';

/**
 * Extract error message from API error response
 * Handles both string errors and Pydantic validation error arrays
 */
export function getErrorMessage(err, fallback = 'An error occurred') {
  const detail = err?.response?.data?.detail;
  
  if (!detail) return fallback;
  
  // If detail is a string, return it directly
  if (typeof detail === 'string') return detail;
  
  // If detail is an array (Pydantic validation errors), extract messages
  if (Array.isArray(detail)) {
    const messages = detail.map(e => {
      if (typeof e === 'string') return e;
      if (e.msg) return e.msg;
      if (e.message) return e.message;
      return JSON.stringify(e);
    });
    return messages.join(', ') || fallback;
  }
  
  // If detail is an object (bid validation errors, etc.)
  if (typeof detail === 'object') {
    // Handle bid validation error format: { message, errors: [...], max_bid_byc, next_valid_bid_byc }
    if (detail.errors && Array.isArray(detail.errors)) {
      const errorMessages = detail.errors.join('. ');
      // Add helpful info about valid bid if available
      if (detail.next_valid_bid_byc) {
        return `${errorMessages}. Next valid bid: ₿${formatBYC(detail.next_valid_bid_byc)}`;
      }
      if (detail.max_bid_byc) {
        return `${errorMessages}. Max allowed: ₿${formatBYC(detail.max_bid_byc)}`;
      }
      return errorMessages || detail.message || fallback;
    }
    
    // Standard object with message
    if (detail.msg) return detail.msg;
    if (detail.message) return detail.message;
    
    // Don't render objects directly
    return fallback;
  }
  
  return fallback;
}

// Hook to get current user and role
export function useCurrentUser() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const auctioneerSession = localStorage.getItem('auctioneer_session');
    
    if (auctioneerSession) {
      try {
        const session = JSON.parse(auctioneerSession);
        // Create auctioneer user object
        setUser({
          name: `Auctioneer (${session.username})`,
          role: 'auctioneer',
          roles: ['auctioneer'],
          isAuctioneer: true,
          eventId: session.eventId,
        });
      } catch (e) {
        console.error('Failed to parse auctioneer session:', e);
      }
    } else if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse user:', e);
      }
    }
    setLoading(false);
  }, []);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || 
                  user?.roles?.includes('admin') || user?.roles?.includes('super_admin');
  const isSuperAdmin = user?.role === 'super_admin' || user?.roles?.includes('super_admin');
  const isAuctioneer = user?.role === 'auctioneer' || user?.roles?.includes('auctioneer') || user?.isAuctioneer;
  const isTeamOwner = user?.roles?.includes('team_owner');

  return { user, loading, isAdmin, isSuperAdmin, isAuctioneer, isTeamOwner };
}

// Hook to fetch auction configuration
export function useAuctionConfig(eventId) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/config`);
      setConfig(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setConfig(null);
      } else {
        setError(getErrorMessage(err, 'Failed to load config'));
      }
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return { config, loading, error, refetch: fetchConfig };
}

// Hook to fetch teams
export function useAuctionTeams(eventId) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTeams = useCallback(async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/teams`);
      setTeams(res.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load teams'));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  return { teams, loading, error, refetch: fetchTeams };
}

// Hook to fetch player pool
export function usePlayerPool(eventId) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPlayers = useCallback(async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/pool`);
      setPlayers(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setPlayers([]);
      } else {
        setError(getErrorMessage(err, 'Failed to load players'));
      }
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  return { players, loading, error, refetch: fetchPlayers };
}

// Hook to fetch live auction stats (with polling)
export function useLiveStats(eventId, pollInterval = 5000) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/live-stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch live stats:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchStats();
    if (pollInterval > 0) {
      const interval = setInterval(fetchStats, pollInterval);
      return () => clearInterval(interval);
    }
  }, [fetchStats, pollInterval]);

  return { stats, loading, refetch: fetchStats };
}

// Hook to fetch current player being auctioned
export function useCurrentPlayer(eventId, pollInterval = 3000) {
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentPlayer = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/current-player`);
      setPlayer(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setPlayer(null);
      }
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchCurrentPlayer();
    if (pollInterval > 0) {
      const interval = setInterval(fetchCurrentPlayer, pollInterval);
      return () => clearInterval(interval);
    }
  }, [fetchCurrentPlayer, pollInterval]);

  return { player, loading, refetch: fetchCurrentPlayer };
}

// Hook for team-specific data (for team owners)
export function useMyTeam(eventId, userId) {
  const [team, setTeam] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [squad, setSquad] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMyTeam = useCallback(async () => {
    if (!eventId || !userId) return;
    try {
      setLoading(true);
      // Get all teams and find the one where current user is an owner
      const teamsRes = await axiosInstance.get(`/v1/events/${eventId}/auction/teams`);
      const myTeam = teamsRes.data.find(t => 
        t.owners?.some(o => o.user_id === userId)
      );
      
      if (myTeam) {
        setTeam(myTeam);
        // Fetch wallet and squad
        const [walletRes, squadRes] = await Promise.all([
          axiosInstance.get(`/v1/events/${eventId}/auction/teams/${myTeam.id}/wallet`),
          axiosInstance.get(`/v1/events/${eventId}/auction/teams/${myTeam.id}/squad`)
        ]);
        setWallet(walletRes.data);
        setSquad(squadRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch team data:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, userId]);

  useEffect(() => {
    fetchMyTeam();
  }, [fetchMyTeam]);

  return { team, wallet, squad, loading, refetch: fetchMyTeam };
}

// Format BYC amount
export function formatBYC(amount) {
  if (amount === null || amount === undefined) return '0';
  return new Intl.NumberFormat('en-IN').format(amount);
}

export function formatBYCCompact(amount) {
  if (amount === null || amount === undefined) return '0';
  const num = Number(amount);
  if (num >= 1_00_00_000) return (num / 1_00_00_000).toFixed(2) + 'Cr';
  if (num >= 1_00_000) return (num / 1_00_000).toFixed(2) + 'L';
  if (num >= 1_000) return (num / 1_000).toFixed(2) + 'k';
  return num.toFixed(2);
}

// Auction status colors
export const AUCTION_STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-600',
  CONFIGURED: 'bg-blue-100 text-blue-600',
  LIVE: 'bg-green-100 text-green-600',
  PAUSED: 'bg-amber-100 text-amber-600',
  CLOSED: 'bg-red-100 text-red-600',
};

// Player status colors
export const PLAYER_STATUS_COLORS = {
  AVAILABLE: 'bg-green-100 text-green-600',
  SOLD: 'bg-blue-100 text-blue-600',
  UNSOLD: 'bg-red-100 text-red-600',
  EXCLUDED: 'bg-gray-100 text-gray-500',
};
